import { yupResolver } from "@hookform/resolvers/yup";
import { endOfMonth } from "date-fns";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button";
import DatePickerInput from "~/components/formElements/DatePickerInput";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import SubmitButton from "~/components/formElements/SubmitButton";
import Hint from "~/components/Hint";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { updateDeliveryPeriod } from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = {
	deliveryEndDate: Date;
	deliveryStartDate: Date;
}

type Props = {
	invoiceData: Invoice;
	onCancel: () => void;
	onSuccess: () => void;
};

const endOfCurrentMonth = endOfMonth(new Date());

const UpdateInvoiceDeliveryPeriodForm = ({
	invoiceData,
	onCancel,
	onSuccess,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const schema = useMemo(() => {
		return yup.object({
			deliveryStartDate: yup.date().required(),
			deliveryEndDate: yup
				.date()
				.required()
				.min(yup.ref("deliveryStartDate"), "Das Startdatum muss vor dem Enddatum liegen.")
				.default(null),
		});
	}, []);
	const defaultValues = useMemo(() => {
		return {
			deliveryStartDate: new Date(invoiceData.deliveryStartDate),
			deliveryEndDate: new Date(invoiceData.deliveryEndDate),
		};
	}, []);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid },
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			await updateDeliveryPeriod({
				invoiceId: invoiceData.id,
				deliveryEndDate: formatDateToYYYYMMDD(data.deliveryEndDate),
				deliveryStartDate: formatDateToYYYYMMDD(data.deliveryStartDate),
			});
			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
	}, [invoiceData.id, onSuccess]);

	const isProjectInvoice = invoiceData.isProjectInvoice;
	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Leistungszeitraum bearbeiten</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update failed">{serverErrorMessage}</SidebarErrorOverlay>}
				{!isProjectInvoice && <div className="grid w-full grid-cols-2 gap-x-4">
					<DatePickerInput
						name="deliveryStartDate"
						clearable={false}
						control={control}
						label="Start"
					/>
					<DatePickerInput
						name="deliveryEndDate"
						clearable={false}
						control={control}
						maxDate={endOfCurrentMonth}
						label="Ende"
					/>
					<div className="col-span-2 mt-4">
						<Hint theme="info"
							  size="sm">
							<p>Der Leistungszeitraum darf nur geändert werden, wenn dieser falsch eingetragen wurde die
								Rechnung noch nicht an den Kunden versendet wurde.</p>

						</Hint>
					</div>
				</div>}
				{isProjectInvoice && <Hint theme="info"
										   title="Bearbeitung nicht möglich"
										   size="sm">
					<p>Der Leistungszeitraum von Projetrechnungen kann nicht nachträglich geändert werden.</p>
					<br />
					<p>Solle eine Änderungen unbedingt nötig sein, muss der Rechnungsvorgang gelöscht und erneut
						gestartet werden.</p>
					<br />
					<p><strong>Achtung:</strong> Hierbei wird die Rechnungsnummer gelöscht und eine neue vergeben.
						Sollte diese Rechnung bereits an den Kunden übermittelt worden sein, darf dies NICHT geschehen,
						da es die Buchhaltung verfälschen würde.</p>
				</Hint>}
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					{t("form:buttonTexts.save")}
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					{t("form:buttonTexts.cancel")}
				</Button>
			</SidebarFooter>
		</form>);
};

export default UpdateInvoiceDeliveryPeriodForm;
